import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Checkbox, Descriptions, Empty, Form, Input, List, message, Modal, Popconfirm, Radio, Space, Spin, Tag, Tooltip, Typography } from 'antd';
import { CloudUploadOutlined, DeleteOutlined, EditOutlined, PlusOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { flightLogRecognitionApi } from './api';

const { Text, Paragraph } = Typography;

const EMPTY_DOMAIN_TEXT = '当前模块暂未启用版本化提示词与规则管理';

const DOMAIN_DETAIL_TAB = {
    flight_log: 'flight-log',
    fuel_form: 'image',
    fuel_report: 'list',
};

const STATUS_META = {
    proposed: { color: 'orange', label: '待审' },
    adopted: { color: 'green', label: '已生效' },
    disabled: { color: 'default', label: '已停用' },
    ignored: { color: 'default', label: '已忽略' },
    superseded: { color: 'default', label: '已被取代' },
};

const OP_LABEL = {
    adopt: '采纳候选',
    create_manual: '手工新建',
    edit_adopted: '编辑已生效规则',
    disable: '停用',
    enable: '重新启用',
};

const RELATION_LABEL = { conflict: '与已生效规则冲突', refine: '细化已生效规则', duplicate: '重复', new: '新增' };

const ruleText = (r) => r?.effective_text || r?.edited_text || r?.rule_text || '';

const RecognitionRulesPanel = ({ domain, onChanged }) => {
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [data, setData] = useState(null);
    const [pending, setPending] = useState([]);
    const [editor, setEditor] = useState(null); // { mode: 'create'|'edit_adopted'|'edit_proposed'|'adopt', rule? }
    const [form] = Form.useForm();
    const [runningRunIds, setRunningRunIds] = useState([]); // 可能覆盖多个组织池，每池一条运行
    const runningRunId = runningRunIds.length > 0 ? runningRunIds[0] : null;
    const [detail, setDetail] = useState(null); // { loading, data }
    const pollTimer = useRef(null);

    const fetchRules = useCallback(async () => {
        setLoading(true);
        try {
            const res = await flightLogRecognitionApi.recognitionRules(domain);
            setData(res.data || res);
        } catch (err) {
            message.error(`加载识别规则失败: ${err?.response?.data?.detail || err.message}`);
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [domain]);

    useEffect(() => {
        setPending([]);
        fetchRules();
    }, [fetchRules]);

    // 挂载/切换域/切回规则页签后，从最近运行记录恢复「立即总结」轮询（复审 P3-3）
    useEffect(() => {
        const running = (data?.recent_runs || []).filter((r) => r.status === 'running').map((r) => r.id);
        if (running.length > 0) {
            setRunningRunIds((prev) => (prev.length > 0 ? prev : running));
        }
    }, [data]);

    const enabled = data?.prompt_enabled === true;
    const rules = data?.rules || {};
    const adoptedIds = useMemo(() => new Set((data?.rules?.adopted || []).map((r) => r.id)), [data]);
    const pendingIds = useMemo(() => new Set(pending.filter((c) => c.rule_id).map((c) => c.rule_id)), [pending]);

    const afterChange = async () => {
        await fetchRules();
        if (onChanged) onChanged();
    };

    const addChange = (change) => {
        if (change.rule_id && pendingIds.has(change.rule_id)) {
            message.warning('该规则已在待发布变更集中');
            return;
        }
        setPending((prev) => [...prev, change]);
    };

    const publish = async (changes) => {
        setBusy(true);
        try {
            const res = await flightLogRecognitionApi.publishRecognitionRules(domain, changes);
            const d = res.data || res;
            message.success(`已发布为新提示词版本 ${d?.version_key || ''}（生效规则 ${d?.rule_count ?? '-'} 条）`);
            setPending([]);
            await afterChange();
        } catch (err) {
            message.error(`发布失败: ${err?.response?.data?.detail || err.message}`);
        } finally {
            setBusy(false);
        }
    };

    const ignore = async (rule) => {
        let note = '';
        Modal.confirm({
            title: '忽略该候选规则？',
            content: <Input.TextArea rows={3} placeholder="忽略原因（可选）" onChange={(e) => { note = e.target.value; }} />,
            okText: '忽略',
            cancelText: '取消',
            onOk: async () => {
                try {
                    await flightLogRecognitionApi.ignoreRecognitionRule(domain, rule.id, { note });
                    message.success('已忽略');
                    await afterChange();
                } catch (err) {
                    message.error(`忽略失败: ${err?.response?.data?.detail || err.message}`);
                }
            },
        });
    };

    // 轮询运行状态直至全部结束（每 3 秒；卸载或切换域时清理，重新挂载后由 recent_runs 恢复）
    const runningKey = runningRunIds.join(',');
    useEffect(() => {
        if (runningRunIds.length === 0) return undefined;
        let cancelled = false;
        const poll = async () => {
            try {
                const results = await Promise.all(runningRunIds.map((id) => flightLogRecognitionApi.recognitionRuleRun(domain, id)));
                if (cancelled) return;
                const runs = results.map((res) => res.data || res);
                const done = runs.filter((run) => run?.status && run.status !== 'running');
                if (done.length > 0) {
                    done.forEach((run) => {
                        const summaryText = `分组 ${run.total_group_count ?? 0} / 过阈值 ${run.qualified_group_count ?? 0} / 送模型 ${run.llm_group_count ?? 0} / 新候选 ${run.proposed_rule_count ?? 0}`;
                        if (run.status === 'failed') {
                            message.error(`总结运行失败：${run.error_summary || '未知错误'}`);
                        } else {
                            message.success(`总结完成（${run.status}）：${summaryText}`);
                        }
                    });
                    const doneIds = new Set(done.map((run) => run.id));
                    setRunningRunIds((prev) => prev.filter((id) => !doneIds.has(id)));
                    afterChange();
                    return;
                }
            } catch (err) {
                if (cancelled) return;
                setRunningRunIds([]);
                message.error(`查询总结运行状态失败: ${err?.response?.data?.detail || err.message}`);
                return;
            }
            pollTimer.current = setTimeout(poll, 3000);
        };
        pollTimer.current = setTimeout(poll, 3000);
        return () => {
            cancelled = true;
            if (pollTimer.current) clearTimeout(pollTimer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runningKey, domain]);

    const openDetail = async (rule) => {
        setDetail({ loading: true, data: rule });
        try {
            const res = await flightLogRecognitionApi.recognitionRuleDetail(domain, rule.id);
            setDetail({ loading: false, data: res.data || res });
        } catch (err) {
            message.error(`加载规则详情失败: ${err?.response?.data?.detail || err.message}`);
            setDetail(null);
        }
    };

    const triggerRun = async () => {
        setBusy(true);
        try {
            const res = await flightLogRecognitionApi.triggerRecognitionRuleRun(domain);
            const body = res.data || res;
            const ids = (Array.isArray(body?.runs) ? body.runs : [body?.run]).filter((r) => r?.id).map((r) => r.id);
            message.info(ids.length > 1 ? `已开始后台总结（${ids.length} 个数据池），正在等待运行结束…` : '已开始后台总结，正在等待运行结束…');
            if (ids.length > 0) setRunningRunIds(ids);
        } catch (err) {
            message.error(`触发总结失败: ${err?.response?.data?.detail || err.message}`);
        } finally {
            setBusy(false);
        }
    };

    const openEditor = (mode, rule) => {
        setEditor({ mode, rule });
        form.setFieldsValue({
            field: rule?.field || '',
            rule_text: rule ? ruleText(rule) : '',
            requires_business_confirm: !!rule?.requires_business_confirm,
            conflict_resolution: undefined,
        });
    };

    const submitEditor = async (immediate) => {
        const values = await form.validateFields();
        const { mode, rule } = editor;
        let change = null;
        if (mode === 'create') {
            change = { op: 'create_manual', field: values.field || null, rule_text: values.rule_text, requires_business_confirm: !!values.requires_business_confirm };
        } else if (mode === 'edit_adopted') {
            change = { op: 'edit_adopted', rule_id: rule.id, rule_text: values.rule_text, requires_business_confirm: !!values.requires_business_confirm };
        } else if (mode === 'adopt') {
            if (values.conflict_resolution === 'coexist' && (values.rule_text || '').trim() === (rule.rule_text || '').trim()) {
                message.error('选择与旧规则并存时须先修改候选文本');
                return;
            }
            change = { op: 'adopt', rule_id: rule.id, edited_text: values.rule_text, requires_business_confirm: !!values.requires_business_confirm };
            if (values.conflict_resolution) change.conflict_resolution = values.conflict_resolution;
        } else if (mode === 'edit_proposed') {
            try {
                await flightLogRecognitionApi.updateProposedRule(domain, rule.id, { edited_text: values.rule_text, requires_business_confirm: !!values.requires_business_confirm });
                message.success('候选文本已保存');
                setEditor(null);
                await afterChange();
            } catch (err) {
                message.error(`保存失败: ${err?.response?.data?.detail || err.message}`);
            }
            return;
        }
        setEditor(null);
        if (immediate) {
            await publish([change]);
        } else {
            addChange(change);
        }
    };

    // 仅当关联的旧规则仍处于已生效状态时才要求选择（与后端 publish 校验一致）
    // 后端沿快照链解析出当前生效的关联规则（conflict_target_rule_id，复审 P3-2）；缺省时退回原关联 ID
    const needsConflictChoice = (rule) => {
        if (!(rule?.relation === 'conflict' || rule?.relation === 'refine') || !rule?.conflict_with_rule_id) return false;
        const target = rule.conflict_target_rule_id !== undefined ? rule.conflict_target_rule_id : rule.conflict_with_rule_id;
        return !!target && adoptedIds.has(target);
    };

    const renderExamples = (rule) => {
        const examples = rule?.evidence?.examples || [];
        if (!examples.length) return null;
        const tab = DOMAIN_DETAIL_TAB[domain];
        return (
            <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>示例：</Text>
                {examples.slice(0, 5).map((e) => (
                    <Tooltip key={e.correction_id} title={`${e.field}: ${e.model_value ?? '-'} → ${e.human_value ?? '-'}`}>
                        <a style={{ marginLeft: 8, fontSize: 12 }} href={`/settlement/fuel-reports?tab=${tab}&detail_id=${encodeURIComponent(e.detail_id)}`} target="_blank" rel="noreferrer">
                            {`${e.model_value ?? '-'} → ${e.human_value ?? '-'}`}
                        </a>
                    </Tooltip>
                ))}
            </div>
        );
    };

    const renderRule = (rule) => {
        const meta = STATUS_META[rule.status] || STATUS_META.superseded;
        const inPending = pendingIds.has(rule.id);
        const actions = [<Button key="detail" size="small" type="link" onClick={() => openDetail(rule)}>详情</Button>];
        if (rule.status === 'proposed') {
            actions.push(<Button key="adopt" size="small" type="link" disabled={inPending} onClick={() => openEditor('adopt', rule)}>采纳</Button>);
            actions.push(<Button key="edit" size="small" type="link" onClick={() => openEditor('edit_proposed', rule)}>编辑</Button>);
            actions.push(<Button key="ignore" size="small" type="link" danger onClick={() => ignore(rule)}>忽略</Button>);
        } else if (rule.status === 'adopted') {
            actions.push(<Button key="edit" size="small" type="link" disabled={inPending} onClick={() => openEditor('edit_adopted', rule)}>编辑</Button>);
            actions.push(<Button key="disable" size="small" type="link" danger disabled={inPending} onClick={() => addChange({ op: 'disable', rule_id: rule.id })}>停用</Button>);
        } else if (rule.status === 'disabled') {
            actions.push(<Button key="enable" size="small" type="link" disabled={inPending} onClick={() => addChange({ op: 'enable', rule_id: rule.id })}>重新启用</Button>);
        }
        return (
            <List.Item actions={actions}>
                <List.Item.Meta
                    title={(
                        <Space size={4} wrap>
                            <Tag color={meta.color}>{meta.label}</Tag>
                            <Tag>{rule.source === 'manual' ? '手工录入' : 'Agent 归纳'}</Tag>
                            <Tag color="blue">{rule.field ? `字段 ${rule.field}` : '全字段'}</Tag>
                            {rule.confidence && <Tag>置信度 {rule.confidence}</Tag>}
                            {rule.status === 'proposed' && rule.relation && rule.relation !== 'new' && <Tag color="red">{RELATION_LABEL[rule.relation] || rule.relation}</Tag>}
                            {rule.status !== 'proposed' && rule.relation === 'refine' && rule.conflict_with_rule_id && <Tag>由旧规则编辑/细化而来</Tag>}
                            {rule.published_version_key && <Tag color="cyan">{`${rule.published_version_key} 引入`}</Tag>}
                            {rule.disabled_version_key && <Tag>{`${rule.disabled_version_key} 停用/替换`}</Tag>}
                            {rule.suggested_kind === 'postprocess' && <Tag color="purple">后处理优化参考</Tag>}
                            {rule.requires_business_confirm && <Tag color="gold">需业务确认</Tag>}
                            {inPending && <Tag color="processing">已加入待发布</Tag>}
                        </Space>
                    )}
                    description={(
                        <>
                            <Paragraph style={{ marginBottom: 4 }}>{ruleText(rule)}</Paragraph>
                            {rule.source !== 'manual' && (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    支撑 {rule.support_count} 条 / {rule.distinct_records} 张单据
                                    {rule.evidence?.previous_ignore_note ? `；上次忽略备注：${rule.evidence.previous_ignore_note}` : ''}
                                </Text>
                            )}
                            {renderExamples(rule)}
                        </>
                    )}
                />
            </List.Item>
        );
    };

    const section = (title, list) => (
        <Card size="small" title={`${title}（${(list || []).length}）`}>
            <List size="small" dataSource={list || []} renderItem={renderRule} locale={{ emptyText: '暂无' }} />
        </Card>
    );

    if (!loading && data && !enabled) {
        return (
            <Card size="small">
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={data?.empty_message || EMPTY_DOMAIN_TEXT} />
                <Space style={{ marginTop: 12 }}>
                    <Button disabled icon={<PlusOutlined />}>手工新建规则</Button>
                    <Button disabled icon={<CloudUploadOutlined />}>合并发布为新版本</Button>
                    <Button disabled icon={<ThunderboltOutlined />}>立即总结</Button>
                </Space>
            </Card>
        );
    }

    const editorRule = editor?.rule;
    return (
        <Spin spinning={loading || busy}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Space wrap>
                    <Button icon={<PlusOutlined />} onClick={() => openEditor('create')}>手工新建规则</Button>
                    <Button icon={<ThunderboltOutlined />} onClick={triggerRun} loading={!!runningRunId} disabled={!!runningRunId}>
                        {runningRunId ? '总结运行中…' : '立即总结'}
                    </Button>
                </Space>

                {pending.length > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        message={`待发布变更 ${pending.length} 项（发布前不修改已生效规则）`}
                        description={(
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                {pending.map((c, idx) => (
                                    <Space key={`${c.op}-${c.rule_id || idx}`}>
                                        <Tag>{OP_LABEL[c.op]}</Tag>
                                        <Text ellipsis style={{ maxWidth: 480 }}>{c.rule_text || c.edited_text || c.rule_id}</Text>
                                        <Button size="small" type="text" icon={<DeleteOutlined />} onClick={() => setPending((prev) => prev.filter((_, i) => i !== idx))} />
                                    </Space>
                                ))}
                                <Space>
                                    <Popconfirm title="确认将以上变更合并发布为一个新提示词版本？" onConfirm={() => publish(pending)} okText="发布" cancelText="取消">
                                        <Button type="primary" size="small" icon={<CloudUploadOutlined />}>合并发布为新版本</Button>
                                    </Popconfirm>
                                    <Button size="small" onClick={() => setPending([])}>清空</Button>
                                </Space>
                            </Space>
                        )}
                    />
                )}

                {section('待审候选', rules.proposed)}
                {section('已生效规则', rules.adopted)}
                {section('已停用规则', rules.disabled)}
                {section('已忽略 / 已被取代', [...(rules.ignored || []), ...(rules.superseded || [])])}

                {Array.isArray(data?.recent_runs) && data.recent_runs.length > 0 && (
                    <Card size="small" title="最近总结运行">
                        {data.recent_runs.slice(0, 5).map((r) => (
                            <div key={r.id} style={{ fontSize: 12 }}>
                                <Text type="secondary">{(r.started_at || '').slice(0, 19).replace('T', ' ')}</Text>
                                {' '}<Tag>{r.trigger_mode}</Tag><Tag>{r.status}</Tag>
                                分组 {r.total_group_count} / 过阈值 {r.qualified_group_count} / 送模型 {r.llm_group_count} / 新候选 {r.proposed_rule_count}
                            </div>
                        ))}
                    </Card>
                )}
            </Space>

            <Modal open={!!detail} title="规则详情" footer={null} onCancel={() => setDetail(null)} width={720} destroyOnHidden>
                <Spin spinning={!!detail?.loading}>
                    {detail?.data && (
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Descriptions size="small" column={2} bordered>
                                <Descriptions.Item label="状态">{(STATUS_META[detail.data.status] || {}).label || detail.data.status}</Descriptions.Item>
                                <Descriptions.Item label="来源">{detail.data.source === 'manual' ? '手工录入' : 'Agent 归纳'}</Descriptions.Item>
                                <Descriptions.Item label="作用范围">{detail.data.field ? `字段 ${detail.data.field}` : '全字段'}{detail.data.error_type ? ` / ${detail.data.error_type}` : ''}</Descriptions.Item>
                                <Descriptions.Item label="置信度">{detail.data.confidence || '-'}</Descriptions.Item>
                                <Descriptions.Item label="引入版本">{detail.data.published_version_key || '-'}</Descriptions.Item>
                                <Descriptions.Item label="停用/替换版本">{detail.data.disabled_version_key || '-'}</Descriptions.Item>
                                <Descriptions.Item label="累计支撑">{`${detail.data.support_count} 条 / ${detail.data.distinct_records} 张单据`}</Descriptions.Item>
                                <Descriptions.Item label="当前仍有效支撑">{detail.data.current_valid_support_count ?? '-'}</Descriptions.Item>
                                <Descriptions.Item label="规则文本" span={2}>{ruleText(detail.data)}</Descriptions.Item>
                                {detail.data.review_note && <Descriptions.Item label="审核备注" span={2}>{detail.data.review_note}</Descriptions.Item>}
                                {detail.data.evidence?.previous_ignore_note && <Descriptions.Item label="上次忽略备注" span={2}>{detail.data.evidence.previous_ignore_note}</Descriptions.Item>}
                            </Descriptions>
                            {renderExamples(detail.data)}
                        </Space>
                    )}
                </Spin>
            </Modal>

            <Modal
                open={!!editor}
                title={{ create: '手工新建规则', edit_adopted: '编辑已生效规则（发布时生成新快照）', edit_proposed: '编辑候选规则', adopt: '采纳候选规则' }[editor?.mode]}
                onCancel={() => setEditor(null)}
                destroyOnHidden
                footer={editor?.mode === 'edit_proposed' ? [
                    <Button key="c" onClick={() => setEditor(null)}>取消</Button>,
                    <Button key="s" type="primary" onClick={() => submitEditor(false)}>保存</Button>,
                ] : [
                    <Button key="c" onClick={() => setEditor(null)}>取消</Button>,
                    <Button key="a" icon={<EditOutlined />} onClick={() => submitEditor(false)}>加入待发布变更</Button>,
                    <Button key="p" type="primary" onClick={() => submitEditor(true)}>立即发布</Button>,
                ]}
            >
                <Form form={form} layout="vertical">
                    {editor?.mode === 'create' && (
                        <Form.Item name="field" label="作用字段（留空 = 全字段通用）">
                            <Input placeholder="如 flight_no" />
                        </Form.Item>
                    )}
                    <Form.Item name="rule_text" label="规则文本" rules={[{ required: true, whitespace: true, message: '请输入规则文本' }]}>
                        <Input.TextArea rows={4} />
                    </Form.Item>
                    <Form.Item name="requires_business_confirm" valuePropName="checked">
                        <Checkbox>需业务确认</Checkbox>
                    </Form.Item>
                    {editor?.mode === 'adopt' && needsConflictChoice(editorRule) && (
                        <Form.Item
                            name="conflict_resolution"
                            label="该候选与已生效规则存在冲突/细化关系，请选择处理方式"
                            rules={[{ required: true, message: '请选择替换旧规则或修改文本后并存' }]}
                        >
                            <Radio.Group>
                                <Radio value="replace">替换旧规则</Radio>
                                <Radio value="coexist">修改文本后与旧规则并存</Radio>
                            </Radio.Group>
                        </Form.Item>
                    )}
                </Form>
            </Modal>
        </Spin>
    );
};

export default RecognitionRulesPanel;
